import "core-js/modules/es6.regexp.split";
import _slicedToArray from "/app/node_modules/@babel/runtime-corejs2/helpers/esm/slicedToArray.js";
import "core-js/modules/web.dom.iterable";
import "core-js/modules/es7.object.entries";
import "core-js/modules/es6.regexp.replace";
import "core-js/modules/es6.string.starts-with";
import "core-js/modules/es7.array.includes";
import "core-js/modules/es6.string.includes";
import "core-js/modules/es6.regexp.match";
/**
 * 路由辅助工具
 * 提供业务环境感知的URL生成和路径解析功能
 */

import router from '@/router';
import store from '@/store';
import { bizContext } from "./biz-context";

/**
 * 获取当前业务代码
 * 统一的业务代码获取逻辑，优先级：
 * 1. URL参数
 * 2. 业务上下文
 * 3. Store
 * 4. Cookie
 * 5. localStorage
 * @returns {string} 当前业务代码
 */
export function getCurrentBizCode() {
  // 优先从路由参数中获取
  var route = router.currentRoute;
  if (route.params && route.params.biz_code && route.params.biz_code !== ':biz_code') {
    console.log("[RouteHelper] Using business code from URL: ".concat(route.params.biz_code));
    return route.params.biz_code;
  }

  // 如果没有路由参数，尝试从URL hash中解析
  if (typeof window !== 'undefined') {
    var match = window.location.hash.match(/\/biz\/([^/]+)/);
    if (match && match[1] && match[1] !== ':biz_code') {
      console.log("[RouteHelper] Using business code from URL hash: ".concat(match[1]));
      return match[1];
    }
  }

  // 其次从业务上下文中获取
  if (bizContext && bizContext.currentBiz) {
    console.log("[RouteHelper] Using business code from context: ".concat(bizContext.currentBiz));
    return bizContext.currentBiz;
  }

  // 再次从 store 中获取
  var storeBizCode = store.state.profile.currBizCode;
  if (storeBizCode) {
    console.log("[RouteHelper] Using business code from store: ".concat(storeBizCode));
    return storeBizCode;
  }

  // 从 sessionStorage 中获取
  var sessionBizCode = sessionStorage.getItem('currBizCode');
  if (sessionBizCode) {
    console.log("[RouteHelper] Using business code from sessionStorage: ".concat(sessionBizCode));
    return sessionBizCode;
  }

  // 最后从 localStorage 中获取
  var localStorageBizCode = localStorage.getItem('last_visited_biz');
  if (localStorageBizCode) {
    console.log("[RouteHelper] Using business code from localStorage: ".concat(localStorageBizCode));
    return localStorageBizCode;
  }
  console.warn('[RouteHelper] No business code found');
  return null;
}

/**
 * 创建带有业务代码的路由路径
 * @param {string} path 原始路径（不包含业务代码）
 * @returns {string} 带有业务代码的完整路径
 */
export function createBizPath(path) {
  var bizCode = getCurrentBizCode();
  if (!bizCode) {
    console.warn('[RouteHelper] No business code available');
    return path;
  }

  // 如果路径已经包含 /biz/，则不做处理
  if (path.includes('/biz/')) {
    return path;
  }

  // 如果路径以 / 开头，则直接添加业务代码
  if (path.startsWith('/')) {
    return "/biz/".concat(bizCode).concat(path);
  }

  // 否则添加前缀 /
  return "/biz/".concat(bizCode, "/").concat(path);
}

/**
 * 路由跳转，自动添加业务代码
 * @param {string|object} location 路由位置
 * @param {boolean} replace 是否使用 replace 模式
 */
export function navigateTo(location) {
  var replace = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  // 如果是字符串，则添加业务代码
  if (typeof location === 'string') {
    location = createBizPath(location);
  } else if (location && location.path) {
    // 如果是对象，且包含 path，则添加业务代码
    location.path = createBizPath(location.path);
  }

  // 使用 router 进行跳转
  if (replace) {
    router.replace(location);
  } else {
    router.push(location);
  }
}

/**
 * 创建带有业务代码的 URL
 * @param {string} path 原始路径（不包含业务代码）
 * @returns {string} 带有业务代码的完整 URL
 */
export function createBizUrl(path) {
  return "/#".concat(createBizPath(path));
}

/**
 * 生成带业务环境的URL（支持路径参数替换）
 * @param {String} path 基础路径 (e.g. '/job/:id')
 * @param {Object} [params] 路径参数
 * @param {String} [bizCode] 指定业务代码 (默认使用当前业务环境)
 * @returns {String} 完整路径
 */
export function generateUrl(path) {
  var params = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var bizCode = arguments.length > 2 ? arguments[2] : undefined;
  var currentBiz = bizCode || getCurrentBizCode();
  if (!currentBiz) {
    console.warn('[RouteHelper] No business context available');
    return path;
  }

  // 确保路径以斜杠开头
  var normalizedPath = path.startsWith('/') ? path : "/".concat(path);

  // 替换路径参数
  var result = "/biz/".concat(currentBiz).concat(normalizedPath);
  for (var _i = 0, _Object$entries = Object.entries(params); _i < _Object$entries.length; _i++) {
    var _Object$entries$_i = _slicedToArray(_Object$entries[_i], 2),
      key = _Object$entries$_i[0],
      value = _Object$entries$_i[1];
    result = result.replace(":".concat(key), encodeURIComponent(value));
  }
  return result;
}

/**
 * 解析路径中的业务代码
 * @param {String} path 完整路径
 * @returns {String|null} 业务代码
 */
export function parseBizCode(path) {
  var match = path.match(/^\/biz\/([^/]+)/);
  return match ? match[1] : null;
}

/**
 * 解析路径参数
 * @param {String} path 完整路径
 * @param {String} pattern 路径模式 (e.g. '/job/:id')
 * @returns {Object} 参数对象
 */
export function parseParams(path, pattern) {
  var result = {};
  var pathParts = path.split('/');
  var patternParts = pattern.split('/');
  for (var i = 0; i < patternParts.length; i++) {
    var part = patternParts[i];
    if (part.startsWith(':')) {
      var paramName = part.slice(1);
      result[paramName] = i < pathParts.length ? decodeURIComponent(pathParts[i]) : null;
    }
  }
  return result;
}

/**
 * 扩展 Vue 原型，添加路由辅助方法
 * @param {object} Vue Vue 实例
 */
export function installRouteHelper(Vue) {
  // 添加全局方法
  Vue.prototype.$bizNavigate = navigateTo;
  Vue.prototype.$bizPath = createBizPath;
  Vue.prototype.$bizUrl = createBizUrl;
  Vue.prototype.$bizCode = getCurrentBizCode;
  Vue.prototype.$generateUrl = generateUrl;
  Vue.prototype.$parseBizCode = parseBizCode;
  Vue.prototype.$parseParams = parseParams;
}

// 仅在开发环境下运行测试
// 以下代码仅用于测试，不会执行
/* istanbul ignore next */
if (process.env.NODE_ENV === 'development') {
  // 测试代码已禁用，需要手动启用
  var runTests = false;
  if (runTests) {
    // 测试generateUrl
    var url1 = generateUrl('/job/:id', {
      id: '123'
    });
    console.assert(url1.includes('/job/123'), 'URL生成失败');

    // 测试parseBizCode
    var biz = parseBizCode('/biz/testBiz/job/123');
    console.assert(biz === 'testBiz', '业务代码解析失败');

    // 测试parseParams
    var params = parseParams('/biz/testBiz/job/123', '/job/:id');
    console.assert(params.id === '123', '参数解析失败');
  }
}